import React, { useMemo, useState } from 'react';
import useUrlState from '../../../hooks/use-url-state';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import Card from '@mui/material/Card';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { ITag } from '../../../ts/interface/problem.interface';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import useSmallScreen from '../../../hooks/useSmallScreen';
import { MovaticFieldGroupText } from '../../../components/Redesing/movatic-field-group';
import CustomToast from '../../../components/Redesing/custom-toast';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import useOrganization from '../../../hooks/use-organization';
import { useMaintenanceTicketTagsRelayQuery } from '../../../hooks/maintenance/query/V2/useMaintenanceTicketTagsRelay';
import { mapMaintenanceTagsToLegacy } from '../../../hooks/maintenance/query/V2/maintenanceMappers';
import {
  createMaintenanceTagMutation,
  updateMaintenanceTagMutation,
} from '../../../hooks/maintenance/mutation/V2/useMaintenanceTagMutations';
import { maintenanceTagColumns } from '../../../tableColumns/maintenanceColumns';
import { ISystem } from '../../../ts/interface/system.interface';
import OrganizationSystemSelect from '../../../components/system/organization-system-select';

function OrganizationMaintenanceTag() {
  const organizationInfo = useOrganization();

  const isSmallScreen = useSmallScreen();

  const [exportData, setExportData] = useState(false);
  const [addTagModal, setAddTagModal] = useState(false);
  const [confirmArchiveModal, setConfirmArchiveModal] = useState(false);
  const [tagToArchive, setTagToArchive] = useState<ITag | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<ISystem | null>(null);

  const [filters, setFilters] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    pageNumber: 0,
    search: '',
    tagName: '',
    status: 'open',
    tags: '',
    sortConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
  });

  const {
    data: tagsDataRaw,
    refetch,
    isLoading,
    isRefetching,
  } = useMaintenanceTicketTagsRelayQuery(
    { organizationId: organizationInfo?.id || undefined },
  );

  const loadedTags = useMemo(
    () => (tagsDataRaw ? mapMaintenanceTagsToLegacy(tagsDataRaw) : ([] as ITag[])),
    [tagsDataRaw]
  );

  const columns: IColumn[] =
    maintenanceTagColumns(setConfirmArchiveModal, setTagToArchive) || [];

  return (
    <Card>
      <PaginationTable
        dataId="maintenance-tags"
        buttons={[
          {
            label: 'Export',
            onClick: () => setExportData(true),
            startIcon: <FileDownloadIcon />,
          },
          {
            startIcon: <AddIcon />,
            label: 'Add',
            dataId: 'add-tag-button',
            onClick: () => setAddTagModal(true),
          },
        ]}
        showFilter={false}
        isLoading={isLoading || isRefetching}
        padding={{ pt: 1, pr: 1 }}
        exportData={exportData}
        csvFileName={'Maintenance_Tags.csv'}
        onExport={() => setExportData(false)}
        columns={columns}
        totalItems={loadedTags?.length || 0}
        items={loadedTags}
        sortBy={filters.sortConfig.key}
        sortDirection={filters.sortConfig.direction}
        onSortChange={(sort) =>
          setFilters({ ...filters, sortConfig: sort as ISortConfig })
        }
        rowsPerPage={filters.sizePerPage}
        page={filters.pageNumber}
        onPageChange={(page, newPage) => setFilters({ ...filters, pageNumber: newPage })}
        onRowsPerPageChange={(pageSizeString) =>
          setFilters({ ...filters, sizePerPage: Number(pageSizeString) })
        }
        searchText={filters.search}
        handleSearch={(search) => setFilters({ ...filters, search: search.target.value })}
      />
      <MovaticCustomModal
        customStyles={{
          content: {
            minWidth: 'none',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: isSmallScreen ? '90vw' : '30vw',
            margin: isSmallScreen ? 1 : 'auto',
            width: '100%',
          },
        }}
        open={addTagModal}
        onClose={() => setAddTagModal(false)}
        content={
          <>
            <OrganizationSystemSelect
              organization_id={organizationInfo?.id}
              onChange={(system: ISystem) => setSelectedSystem(system)}
              selectedSystem={{
                value: selectedSystem?.gid || '',
                name: selectedSystem?.name || '',
              }}
            />
            <MovaticFieldGroupText
              label="Tag Name"
              placeholder="Name"
              description="(Required)"
              name="name"
              value={filters.tagName}
              dataId="tag-name"
              onChange={(e) => setFilters({ ...filters, tagName: e.target.value })}
            />
          </>
        }
        title={'Add New Tag'}
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setAddTagModal(false),
              },
              {
                label: 'Save',
                dataId: 'save-tag-button',
                onClick: () => {
                  if (!filters.tagName) {
                    CustomToast({
                      type: 'error',
                      message: 'Tag name is required',
                    });
                    return;
                  }

                  if (!selectedSystem) {
                    CustomToast({
                      type: 'error',
                      message: 'System is required',
                    });
                    return;
                  }

                  CustomAsyncToast({
                    promise: () =>
                      createMaintenanceTagMutation({
                        name: filters.tagName,
                        systemId: selectedSystem?.gid,
                      }).then((result) => {
                        if (!result.isSuccess) {
                          throw new Error(
                            result.errors[0]?.message || 'Failed to add tag'
                          );
                        }
                        return result;
                      }),
                    successMessage: () => 'Tag added successfully',
                    errorMessage: 'Error adding tag',
                    loadingMessage: 'Adding tag...',
                  }).then((r) => {
                    if (r) {
                      refetch();
                      setFilters({ ...filters, tagName: '' });
                      setAddTagModal(false);
                    }
                  });
                },
              },
            ]}
          />
        }
      />
      <MovaticCustomModal
        customStyles={{
          content: {
            minWidth: 'none',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: isSmallScreen ? '90vw' : '30vw',
            margin: isSmallScreen ? 1 : 'auto',
            width: '100%',
          },
        }}
        open={confirmArchiveModal}
        onClose={() => setConfirmArchiveModal(false)}
        content={`Are you sure you want to archive the tag ${tagToArchive?.name}?`}
        title={'Archive Tag'}
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setConfirmArchiveModal(false),
              },
              {
                label: 'Submit',
                onClick: () => {
                  if (tagToArchive?.id) {
                    CustomAsyncToast({
                      promise: () =>
                        updateMaintenanceTagMutation(tagToArchive.id, {
                          archivedOn: new Date().toISOString(),
                        }).then((result) => {
                          if (!result.isSuccess) {
                            throw new Error(
                              result.errors[0]?.message ||
                                'Failed to archive tag'
                            );
                          }
                          return result;
                        }),
                      successMessage: () => 'Tag archived successfully',
                      errorMessage: 'Error archiving tag',
                      loadingMessage: 'Archiving tag...',
                    }).then((r) => {
                      if (r) {
                        refetch();
                        setConfirmArchiveModal(false);
                        setTagToArchive(null);
                      }
                    });
                  }
                },
              },
            ]}
          />
        }
      />
    </Card>
  );
}

export default OrganizationMaintenanceTag;
